import Button from "@amzn/meridian/button";
import Message from "@amzn/meridian/message";
import CsvTable from "src/components/shared/csvTable/csvTable";
import { formatISO } from "date-fns";
import { useMemo } from "react";
import Plot from "react-plotly.js";
import ResponseConfirmation from "src/components/pages/home/components/chat/chatResult/responseConfirmation";
import BotMessage from "src/components/shared/botMessage/botMessage";
import EmptyChartMessage from "src/components/shared/emptyChartMessage/EmptyChartMessage";
import DenyConfirmationMessage from "src/components/shared/denyConfirmationMessage/denyConfirmationMessage";
import { BADGE_PHOTO_THUMBNAIL_URL } from "src/config/external-links";
import { DEFAULT_PLOTLY_CONFIG } from "src/constants/constants";
import chatIcon from "src/images/safer_chat_icon.png";
import { useAppSelector } from "src/store/store";
import { ChatResponse } from "../../../store/types";
import Vote from "../vote/vote";
import "./chatResult.scss";
import CitationText from "src/components/shared/citationText/citationText";
import { processCitations } from "src/utils/citationUtils";
import SourcesDropdown from "src/components/shared/sourcesDropDown/sourcesDropdown";
import { Icon, Row, Text } from "@amzn/meridian";
import sparklesLargeTokens from "@amzn/meridian-tokens/base/icon/sparkles-large";
import { formatTime } from "src/utils/dateUtils";
import NoSaigeAnswerMessage from "src/components/shared/noSaigeAnswerMessage/NoSaigeAnswerMessage";
interface ChatResultProps {
  output: ChatResponse;
  isOnMcpRoute: boolean;
}
const ChatResult = ({ output, isOnMcpRoute }: ChatResultProps) => {
  const { alias } = useAppSelector((state) => state.user);
  const { streaming } = useAppSelector((state) => state.chatSlice);
  const botName = !isOnMcpRoute ? "Saige" : "Safer";
  // We treat a response as "no answer" when either of the following is true:
  //  1. There are no related documents at all.
  //  2. There are no *cited* documents in the LLM answer. Amazon Q Business sometimes returns
  //     reference documents (relatedDocuments) that are never actually cited in the answer text,
  //     and generic fallbacks (e.g. "No answer available" or "Sorry, I could not find relevant
  //     information...") carry no citations either. We rely on sourceAttributions (which carry the
  //     in-answer citation segments used to render [n] markers) to decide whether the answer is
  //     genuinely grounded in a WHS procedure.
  // In these cases we show NoSaigeAnswerMessage instead of the raw backend text.
  // Note: backend responses are passed through as-is from Amazon Q Business without modification.
  const hasCitedDocument = (output.sourceAttributions ?? []).some(
    (attr) => (attr.textMessageSegments?.length ?? 0) > 0,
  );
  const isNoAnswer = !output.relatedDocuments?.length || !hasCitedDocument;
  const chartJson: any = useMemo(() => {
    if (output?.chartResult?.length) {
      return JSON.parse(output.chartResult);
    }
  }, [output.chartResult]);

  // Don't render if this is a streaming message that's currently being processed
  if (
    isOnMcpRoute &&
    streaming.isStreaming &&
    output.messageId === streaming.messageId
  ) {
    return null;
  }

  return (
    <div>
      <Row spacing={"200"} spacingInset={"none none 300 none"}>
        <Icon tokens={sparklesLargeTokens} color="success" />
        <Text type="h100">SAIGE</Text>
        <Text type="b100">{formatTime(output.datetime || new Date())}</Text>
      </Row>
      {output.isTextOnly ? (
        <div className="user-bot-response">
          {isNoAnswer ? (
            <NoSaigeAnswerMessage />
          ) : (
            <>
              <CitationText
                text={processCitations(
                  output.llmMessage,
                  output.sourceAttributions,
                )}
                citations={output.sourceAttributions}
              />
              {output.relatedDocuments && (
                <SourcesDropdown
                  relatedDocuments={output.relatedDocuments}
                  sourceAttributions={output.sourceAttributions}
                />
              )}
            </>
          )}
          <Row alignmentHorizontal="end">
            <Vote
              messageId={output.messageId}
              llmResponse={output.llmResponse}
              voteResponse={output.voteResponse}
              thumbsUpClicked={output.thumbsUpClicked}
              thumbsDownClicked={output.thumbsDownClicked}
            />
          </Row>
        </div>
      ) : (
        <div className="chat-result-container">
          <div className="result-confirmation-message user-bot-response">
            <Message
              name={botName}
              datetime={output.datetime || formatISO(new Date())}
              avatarImageSource={chatIcon}
              type="outgoing"
              maxWidth="750px"
            >
              {output.llmMessage}
              <ResponseConfirmation output={output} />
            </Message>
          </div>
          {output.confirmationYesClicked && (
            <>
              <div className="user-bot-response">
                <Message
                  name={`${alias}`}
                  datetime={output.datetime || formatISO(new Date())}
                  avatarImageSource={`${BADGE_PHOTO_THUMBNAIL_URL}${alias}`}
                  type="incoming"
                  maxWidth="750px"
                >
                  <Button disabled={true}>Yes</Button>
                </Message>
              </div>
              <BotMessage datetime={output.datetime || formatISO(new Date())}>
                {output.isEmptyData ? (
                  <EmptyChartMessage containerWidth="660px" />
                ) : (
                  <>
                    {output.numericalAnswer && (
                      <div style={{ width: "660px", marginBottom: "20px" }}>
                        {output.numericalAnswer}
                      </div>
                    )}

                    {output.showCsv && output.csvPreview ? (
                      <CsvTable
                        csvPreview={output.csvPreview}
                        csvData={output.csvData}
                      />
                    ) : (
                      output.chartResult &&
                      chartJson && (
                        <Plot
                          style={{ width: "700px", height: "450px" }}
                          data={chartJson.data}
                          layout={chartJson.layout}
                          config={DEFAULT_PLOTLY_CONFIG}
                        />
                      )
                    )}
                  </>
                )}
                <Vote
                  messageId={output.messageId}
                  llmResponse={output.llmResponse}
                  voteResponse={output.voteResponse}
                  thumbsUpClicked={output.thumbsUpClicked}
                  thumbsDownClicked={output.thumbsDownClicked}
                />
              </BotMessage>
            </>
          )}
          {output.confirmationNoClicked && (
            <>
              <Message
                name={`${alias}`}
                datetime={output.datetime || formatISO(new Date())}
                avatarImageSource={`${BADGE_PHOTO_THUMBNAIL_URL}${alias}`}
                type="incoming"
                maxWidth="750px"
              >
                <Button
                  disabled={true}
                  className="confirmation-button-clicked-no"
                >
                  No
                </Button>
              </Message>
              <DenyConfirmationMessage />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatResult;
